import { createErrorMonitor } from './error-monitor';
import { toError } from '../common/error';
import { getSimulateWidgetError, simulateControllerError, } from './simulate-error';
import { getRegionalSettings, getSiteLanguage, getUserData, isSSR, } from './wix-code-api';
import once from 'once';
import { getAppParams } from './app-params';
import { ControllerError } from './errors';
import { normalizeFormIds } from './normalize-form-ids';
import { requestCaptchaToken } from '../../services/captcha-token';
import { validateForm } from './validate-form';
import { hasForm } from './has-form';
import { getInitialData } from './get-initial-data';
import { isFormDisabledBuilder, isFormEmptyBuilder } from './form-utils';
import { getFieldPropertiesByTarget, getFormName, getFormSteps, } from '@wix/form-schema';
import { getSerializableHttpError, isHttpError } from './http-error';
import { defaultLocaleDatasetLoader, loadTranslations, } from '../../services/load-translations';
import { isEmpty } from './is-empty';
import { INTERACTIONS } from '../../features/monitoring/interactions';
import { createControllerActions } from './create-controller-actions';
import { FORM_ASYNC_ACTIONS } from '../../form-actions';
import { FIELD_ASYNC_ACTIONS } from '@wix/form-fields/controller';
import { loadAdditionalForms, getLocaleDataset, getAddressTemplates, getFieldInitialData, } from './load-forms';
export { Kind as FormKind } from '@wix/ambassador-forms-v4-form/types';
const simulateErrorOnce = once(simulateControllerError);
export const initFormController = async (flowAPI, { formId, formKind, namespace, throwWhenFormMissing = true, additionalMetadata, localeDatasetLoader, }) => {
    const { controllerConfig, environment, httpClient, panoramaClient, errorHandler, bi, } = flowAPI;
    panoramaClient
        ?.transaction(INTERACTIONS.LOAD_VIEWER_CONTROLLER)
        .start({ namespace });
    const errorMonitor = createErrorMonitor(controllerConfig);
    const { wixCodeApi, platformAPIs, appParams, essentials } = controllerConfig;
    const setProps = controllerConfig.setProps;
    const { appVersion, appName, appDefinitionId } = getAppParams(appParams);
    try {
        simulateErrorOnce(controllerConfig);
        const language = getSiteLanguage(wixCodeApi);
        const formIds = toFormIds(formId, errorMonitor);
        const warmupDataKey = `form-viewer-${controllerConfig.compId}`;
        const shouldCreateWarmupData = isSSR(wixCodeApi);
        let data;
        if (shouldCreateWarmupData) {
            data = await getInitialData({
                formIds,
                formKind,
                namespace,
                throwWhenFormMissing,
                language,
                localeDatasetLoader,
                httpClient,
                errorMonitor,
                additionalMetadata,
                wixCodeApi,
                errorHandler,
                environment,
                bi,
                appDefinitionId,
            });
            wixCodeApi.window.warmupData.set(warmupDataKey, data);
        }
        else {
            data =
                wixCodeApi.window.warmupData.get(warmupDataKey) ??
                    (await getInitialData({
                        formIds,
                        formKind,
                        namespace,
                        throwWhenFormMissing,
                        language,
                        localeDatasetLoader,
                        httpClient,
                        errorMonitor,
                        additionalMetadata,
                        wixCodeApi,
                        errorHandler,
                        environment,
                        bi,
                        appDefinitionId,
                    }));
            if (isEmpty(data.localeDataset)) {
                const forms = Object.values(data.formsById).filter(Boolean);
                localeDatasetLoader ??= defaultLocaleDatasetLoader(forms, language, errorMonitor);
                data.localeDataset = await localeDatasetLoader();
            }
            if (isEmpty(data.translations)) {
                data.translations = await loadTranslations(language, errorMonitor);
            }
        }
        const userData = data.userData;
        const instance = wixCodeApi.site.getAppToken?.(appDefinitionId);
        let wfConfig = {
            instance,
            language,
            translations: data.translations,
            localeDataset: data.localeDataset,
            formsById: data.formsById,
            addressTemplates: data.addressTemplates,
            simulateError: getSimulateWidgetError(controllerConfig),
            isSSR: isSSR(wixCodeApi),
            appVersion,
            appName,
            appDefinitionId,
            isMobile: wixCodeApi.window.formFactor === 'Mobile',
            isEditor: environment.isEditor,
            isComposer: false,
            isRTL: environment.isRTL,
            experiments: essentials.experiments.all(),
            currency: getCurrency(flowAPI),
            regionalFormat: getRegionalSettings(wixCodeApi),
            userData,
            fieldInitialData: data.fieldInitialData,
            compId: controllerConfig.compId,
            baseUrl: wixCodeApi.location.baseUrl,
        };
        const controllerActions = createControllerActions({
            formActions: {
                ...FIELD_ASYNC_ACTIONS,
                ...FORM_ASYNC_ACTIONS,
            },
            formActionApis: {
                httpClient,
                requestCaptcha: requestCaptchaToken({ wixCodeApi, platformAPIs }),
                wixCodeApi,
                errorHandler,
            },
            setProps,
            resolveError,
        });
        setProps({
            wfConfig,
            wfActions: controllerActions,
            wfResults: {},
        });
        wixCodeApi.user.onLogin(async () => {
            const newUserData = await getUserData(wixCodeApi, {
                appDefinitionId,
                httpClient,
            });
            if (newUserData) {
                wfConfig = { ...wfConfig, userData: newUserData };
                setProps({ wfConfig });
            }
        });
        const loadForms = async ({ namespace: loadNamespace, formIds: loadFormIds, additionalMetadata: loadAdditionalMetadata, formKind: loadFormKind = formKind, }) => {
            const newFormsById = await loadAdditionalForms({
                namespace: loadNamespace,
                formIds: loadFormIds,
                additionalMetadata: loadAdditionalMetadata,
                formKind: loadFormKind,
                httpClient,
                errorMonitor,
                errorHandler,
                language,
            });
            if (!newFormsById) {
                return;
            }
            const newForms = Object.values(newFormsById).filter(Boolean);
            const mergedFormsById = {
                ...wfConfig.formsById,
                ...newFormsById,
            };
            const allForms = Object.values(mergedFormsById).filter(Boolean);
            const [updatedLocaleDataset, updatedAddressTemplates, newFieldInitialData,] = await Promise.all([
                getLocaleDataset({
                    allForms,
                    language,
                    localeDatasetLoader,
                    errorMonitor,
                }),
                getAddressTemplates({
                    forms: newForms,
                    existingAddressTemplates: wfConfig.addressTemplates,
                    httpClient,
                    errorHandler,
                }),
                getFieldInitialData({
                    forms: newForms,
                    httpClient,
                    errorHandler,
                    wixCodeApi,
                    environment,
                    bi,
                }),
            ]);
            wfConfig = {
                ...wfConfig,
                formsById: mergedFormsById,
                localeDataset: updatedLocaleDataset,
                addressTemplates: updatedAddressTemplates ?? wfConfig.addressTemplates,
                fieldInitialData: {
                    ...wfConfig.fieldInitialData,
                    ...newFieldInitialData,
                },
            };
            setProps({ wfConfig });
        };
        return {
            isFormEmpty: isFormEmptyBuilder(data.formsById),
            isFormDisabled: isFormDisabledBuilder(data.formsById),
            validateForm: validateForm(data.formsById, wfConfig.addressTemplates),
            hasForm: hasForm(data.formsById),
            getFieldPropertiesByTarget: (_formId, options) => {
                const form = data.formsById[_formId];
                return form ? getFieldPropertiesByTarget(form, options) : undefined;
            },
            getFormName: (_formId) => {
                const form = data.formsById[_formId];
                return form ? getFormName(form) : undefined;
            },
            getFormSteps: (_formId) => {
                const form = data.formsById[_formId];
                return form ? getFormSteps(form) : undefined;
            },
            loadForms,
        };
    }
    catch (error) {
        if (error instanceof ControllerError) {
            throw error;
        }
        console.error(error);
        const errorWithMessage = toError(error);
        const errorId = errorMonitor.captureException(errorWithMessage);
        console.error(`Error id: ${errorId}`);
        const controllerError = { id: errorId, message: errorWithMessage.message };
        setProps({
            wfConfig: {
                controllerError,
            },
            wfActions: {},
            wfResults: {},
        });
        throw error;
    }
    finally {
        panoramaClient
            ?.transaction(INTERACTIONS.LOAD_VIEWER_CONTROLLER)
            .finish({ namespace });
    }
};
function getCurrency(flowAPI) {
    const { controllerConfig, getCurrencyFormatter } = flowAPI;
    const { wixCodeApi } = controllerConfig;
    const currency = wixCodeApi.site.currency;
    if (!currency) {
        return {};
    }
    if (!getCurrencyFormatter) {
        return {};
    }
    const formattedCurrencyParts = getCurrencyFormatter({ parts: true })({
        currency,
        value: '',
    });
    return {
        sign: formattedCurrencyParts.find((part) => part.type === 'currency')
            ?.value,
        code: currency,
    };
}
function toFormIds(formId, errorMonitor) {
    try {
        return normalizeFormIds(formId);
    }
    catch (error) {
        errorMonitor.captureException(toError(error));
        throw new ControllerError('Invalid formId', error);
    }
}
function resolveError(error) {
    return isHttpError(error) ? getSerializableHttpError(error) : error;
}
//# sourceMappingURL=controller.js.map